import React, { useEffect, useRef } from 'react';
import { WfnRadioButton } from '@adp-wfn/mdf-wc-react';
import { resolveAriaProperty } from '../util/resolveAriaProperty';

export interface IMDFRadioProps {
  'aria-describedby'?: string;
  ariaDescribedby?: string;
  'aria-invalid'?: boolean;
  ariaInvalid?: boolean;
  'aria-label'?: string;
  ariaLabel?: string;
  'aria-required'?: boolean;
  ariaRequired?: boolean;
  children?: React.ReactNode;
  className?: string;
  description?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  name?: string;
  radioGroupValue?: any;
  required?: boolean;
  style?: React.CSSProperties;
  tabIndex?: number;
  value?: any;
  onBlur?: (event?) => void;
  onChange?: (value: any) => void;
  onFocus?: (event?) => void;
}

export const MDFRadio = (props: IMDFRadioProps) => {
  const {
    children,
    className,
    description,
    disabled,
    id,
    label,
    name,
    radioGroupValue,
    required,
    style,
    tabIndex,
    value,
    onBlur,
    onChange,
    onFocus
  } = props;

  if (style) {
    console.warn('MDFRadio: The "style" prop is deprecated. Please use "className" instead.');
  }

  const radioRef = useRef<HTMLWfnRadioButtonElement>(null);
  const resolvedAriaDescribedby = resolveAriaProperty('MDFRadio', 'aria-describedby', 'ariaDescribedby', props);
  const resolvedAriaInvalid = resolveAriaProperty('MDFRadio', 'aria-invalid', 'ariaInvalid', props);
  const resolvedAriaLabel = resolveAriaProperty('MDFRadio', 'aria-label', 'ariaLabel', props);
  const resolvedAriaRequired = resolveAriaProperty('MDFRadio', 'aria-required', 'ariaRequired', props);

  // We need to make sure that we don't overwrite the Stencil 'hydrated' class with application class changes.
  let effectiveClassName = className;

  const handleSdfChange = (event: CustomEvent) => {
    onChange?.(event.detail.value);
  };

  const handleFocus = (event) => {
    onFocus?.(event);
  };

  const handleBlur = (event) => {
    onBlur?.(event);
  };

  useEffect(() => {
    if (radioRef.current) {
      if (radioRef.current.classList.contains('hydrated')) {
        // Make sure the Stencil 'hydrated' class isn't removed by application class changes.
        effectiveClassName = `${className} hydrated`;
      }
    }
  }, [className]);

  const wfnProps: any = {
    'aria-describedby': resolvedAriaDescribedby,
    'aria-invalid': resolvedAriaInvalid,
    'aria-label': resolvedAriaLabel,
    'aria-required': resolvedAriaRequired || required,
    class: effectiveClassName,
    description,
    disabled,
    id,
    label,
    name,
    radioGroupValue,
    required,
    style,
    tabIndex,
    value
  };

  // Remove undefined props so the web component doesn't receive attribute="undefined".
  Object.keys(wfnProps).forEach((key) => {
    if (wfnProps[key] === undefined) {
      delete wfnProps[key];
    }
  });

  return (
    <WfnRadioButton
      {...wfnProps}
      ref={radioRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onSdfChange={handleSdfChange}
    >
      {children}
    </WfnRadioButton>
  );
};

MDFRadio.displayName = 'MDFRadio';
